 
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Users, TrendingUp, DollarSign, AlertCircle, CheckCircle, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TripFinancePage() {
  // State management
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripFinance, setTripFinance] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [collections, setCollections] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchTrips = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/trips`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch trips');
      const data = await response.json();
      const tripList = Array.isArray(data) ? data : [];
      setTrips(tripList);
      if (tripList.length > 0) {
        setSelectedTrip(tripList[0]);
      }
    } catch (error) {
      console.error('Error fetching trips:', error);
      setTrips([]);
      toast({ title: 'Error', description: 'Failed to load trips' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchTripFinance = useCallback(async () => {
    if (!selectedTrip?.id) return;
    try {
      const response = await fetch(`${API}/trips/${selectedTrip.id}/budget-summary`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch trip finance');
      const data = await response.json();
      setTripFinance(data || null);
    } catch (error) {
      console.error('Error fetching trip finance:', error);
      setTripFinance(null);
    }
  }, [selectedTrip?.id]);

  const fetchEligibility = useCallback(async () => {
    if (!selectedTrip?.id) return;
    try {
      const response = await fetch(`${API}/trips/${selectedTrip.id}/eligible-students`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch eligibility');
      const data = await response.json();
      setEligibility(data || null);
    } catch (error) {
      console.error('Error fetching eligibility:', error);
      setEligibility(null);
    }
  }, [selectedTrip?.id]);

  const fetchCollections = useCallback(async () => {
    if (!selectedTrip?.id) return;
    try {
      const response = await fetch(`${API}/trips/${selectedTrip.id}/collections`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch collections');
      const data = await response.json();
      setCollections(Array.isArray(data) ? data : data.payments || data.students || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
      setCollections([]);
    }
  }, [selectedTrip?.id]);

  const fetchExpenses = useCallback(async () => {
    if (!selectedTrip?.id) return;
    try {
      const response = await fetch(`${API}/trips/${selectedTrip.id}/expenses`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch expenses');
      const data = await response.json();
      setExpenses(Array.isArray(data) ? data : (Array.isArray(data?.expenses) ? data.expenses : []));
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setExpenses([]);
    }
  }, [selectedTrip?.id]);

  // Fetch trips on mount
  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  // Fetch trip details when trip is selected
  useEffect(() => {
    if (selectedTrip) {
      fetchTripFinance();
      fetchEligibility();
      fetchCollections();
      fetchExpenses();
    }
  }, [selectedTrip, fetchTripFinance, fetchEligibility, fetchCollections, fetchExpenses]);

  const handleConfirmEligibleStudents = async () => {
    try {
      const response = await fetch(`${API}/trips/${selectedTrip.id}/confirm-eligible-students`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to confirm students');
      toast({ title: 'Success', description: 'Eligible students confirmed for trip' });
      fetchEligibility();
    } catch (error) {
      console.error('Error confirming students:', error);
      toast({ title: 'Error', description: error.message });
    }
  };

  const handleExportReport = async () => {
    try {
      const response = await fetch(`${API}/trips/${selectedTrip.id}/budget-summary`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to export report');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trip-budget-${selectedTrip.id}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: 'Success', description: 'Report exported successfully' });
    } catch (error) {
      console.error('Error exporting report:', error);
      toast({ title: 'Error', description: error.message });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-600">Loading trip finance data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Trip Finance Management</h1>
          <p className="text-gray-600 mt-1">Track trip budgets, collections, and expenses</p>
        </div>
        <Button onClick={handleExportReport} className="bg-blue-600 hover:bg-blue-700">
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Trip Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Trip
              </label>
              <select
                className="w-full border rounded px-3 py-2"
                value={selectedTrip?.id || ''}
                onChange={(e) => setSelectedTrip(trips.find(t => t.id === e.target.value))}
              >
                <option value="">Choose a trip...</option>
                {trips.map((trip) => (
                  <option key={trip.id} value={trip.id}>
                    {trip.name || trip.title} - {new Date(trip.tripDate || trip.trip_date).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedTrip && tripFinance && (
        <>
          {/* Budget Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-600 text-sm">Expected Income</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ZWL {Number(tripFinance.expectedIncome || 0).toFixed(2)}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-600 text-sm">Actual Collections</p>
                    <p className="text-2xl font-bold text-green-600">
                      ZWL {Number(tripFinance.actualIncome || 0).toFixed(2)}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-600 text-sm">Expected Expenses</p>
                    <p className="text-2xl font-bold text-orange-600">
                      ZWL {Number(tripFinance.expectedExpenses || 0).toFixed(2)}
                    </p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-600 text-sm">Actual Balance</p>
                    <p className={`text-2xl font-bold ${
                      (tripFinance.actualBalance || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ZWL {Number(tripFinance.actualBalance || 0).toFixed(2)}
                    </p>
                  </div>
                  <CheckCircle className={`w-8 h-8 ${
                    (tripFinance.actualBalance || 0) >= 0 ? 'text-green-500' : 'text-red-500'
                  }`} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Student Eligibility Section */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Student Eligibility</CardTitle>
                <Button onClick={handleConfirmEligibleStudents} className="bg-green-600 hover:bg-green-700">
                  Confirm Eligible Students
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {eligibility && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 p-4 rounded">
                    <p className="text-sm text-gray-600">Fully Eligible</p>
                    <p className="text-3xl font-bold text-green-600">
                      {eligibility.eligible?.length || 0}
                    </p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded">
                    <p className="text-sm text-gray-600">Partial Payment</p>
                    <p className="text-3xl font-bold text-yellow-600">
                      {eligibility.partialEligible?.length || 0}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded">
                    <p className="text-sm text-gray-600">Not Eligible</p>
                    <p className="text-3xl font-bold text-red-600">
                      {eligibility.ineligible?.length || 0}
                    </p>
                  </div>
                </div>
              )}

              {eligibility?.eligible && eligibility.eligible.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold mb-3 text-green-900">Eligible Students ({eligibility.eligible.length})</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {eligibility.eligible.map((student) => (
                      <div key={student.id} className="flex justify-between p-2 bg-green-50 rounded">
                        <span className="text-sm">{student.name}</span>
                        <span className="text-xs text-green-600">✓ Confirmed</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {eligibility?.ineligible && eligibility.ineligible.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 text-red-900">Ineligible Students ({eligibility.ineligible.length})</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {eligibility.ineligible.map((student) => (
                      <div key={student.id} className="flex justify-between p-2 bg-red-50 rounded">
                        <span className="text-sm">{student.name}</span>
                        <span className="text-xs text-red-600">{student.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Income Chart */}
          {collections.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Collections Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Paid', value: tripFinance.actualIncome || 0 },
                        { name: 'Outstanding', value: Math.max(0, (tripFinance.expectedIncome || 0) - (tripFinance.actualIncome || 0)) },
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ZWL ${value.toFixed(2)}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      <Cell fill="#10b981" />
                      <Cell fill="#ef4444" />
                    </Pie>
                    <Tooltip formatter={(value) => `ZWL ${value.toFixed(2)}`} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Collections Table */}
          <Card>
            <CardHeader>
              <CardTitle>Student Collections</CardTitle>
            </CardHeader>
            <CardContent>
              {collections.length === 0 ? (
                <p className="text-gray-600 py-4">No collections recorded</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Student</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Amount</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Date Paid</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collections.map((collection) => (
                        <tr key={collection.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">{collection.studentName}</td>
                          <td className="py-3 px-4 font-semibold">ZWL {collection.amount.toFixed(2)}</td>
                          <td className="py-3 px-4">{new Date(collection.paymentDate).toLocaleDateString()}</td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              collection.status === 'Paid'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {collection.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expenses Table */}
          <Card>
            <CardHeader>
              <CardTitle>Trip Expenses</CardTitle>
            </CardHeader>
            <CardContent>
              {expenses.length === 0 ? (
                <p className="text-gray-600 py-4">No expenses recorded</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Category</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Description</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Amount</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map((expense) => (
                        <tr key={expense.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">{expense.category}</td>
                          <td className="py-3 px-4 text-sm">{expense.description}</td>
                          <td className="py-3 px-4 font-semibold">ZWL {expense.amount.toFixed(2)}</td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              expense.status === 'Paid'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {expense.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
